import { useState, useCallback, useMemo } from 'react';
import { useGateway } from './useGateway';
import { Card, PaginatedCards, CardFilters } from '../types/card';
import { dataCache } from '../helpers/dataCache';
import { ProductUpdateSubscribeInput, ProductUpdateSubscriptionView } from '../types/productUpdates';

/**
 * Hook for card operations using the service-based architecture
 */
export const useCardsService = () => {
  const { gateway, handleRequest, isLoading, error } = useGateway();
  const [cards, setCards] = useState<Card[]>([]);
  const [card, setCard] = useState<Card | null>(null);

  // Memoize gateway.cards to prevent unnecessary re-renders
  const cardsGateway = useMemo(() => gateway.cards, [gateway.cards]);

  /**
   * Get all cards with optional filtering
   */
  const getCards = useCallback(async (options: CardFilters = {}): Promise<PaginatedCards | null> => {
    const result = await handleRequest(() => cardsGateway.getCards(options));
    if (result?.items) {
      setCards(result.items);
    }
    return result;
  }, [cardsGateway, handleRequest]);

  /**
   * Get card by brand name
   */
  const getCardByBrand = useCallback(async (brand: string): Promise<Card | null> => {
    const result = await handleRequest(() => cardsGateway.getCardByBrand(brand));
    if (result) {
      setCard(result);
    }
    return result;
  }, [cardsGateway, handleRequest]);

  /**
   * Get card categories with localStorage cache
   */
  const getCategories = useCallback(async (): Promise<string[] | null> => {
    const cached = dataCache.getCategories();
    if (cached) return cached;
    
    const result = await handleRequest(() => cardsGateway.getCategoriesAsStrings());
    if (result) dataCache.setCategories(result);
    return result;
  }, [cardsGateway, handleRequest]);

  /**
   * Get card countries with localStorage cache
   */
  const getCountries = useCallback(async (): Promise<string[] | null> => {
    // Check cache first
    const cached = dataCache.getCountries();
    if (cached) {
      return cached;
    }

    // Fetch from API and cache (using legacy method that returns strings)
    const result = await handleRequest(() => cardsGateway.getCountries());
    if (result) {
      dataCache.setCountries(result);
    }
    return result;
  }, [cardsGateway, handleRequest]);

  /**
   * Search cards
   */
  const searchCards = useCallback(async (query: string): Promise<Card[] | null> => {
    return await handleRequest(() => cardsGateway.searchCards(query));
  }, [cardsGateway, handleRequest]);

  /**
   * Get cards with same brand
   */
  const getCardsWithSameBrand = useCallback(async (id: string): Promise<Card[] | null> => {
    return await handleRequest(() => cardsGateway.getCardsWithSameBrand(id));
  }, [cardsGateway, handleRequest]);

  /**
   * Subscribe to a card's updates. Rethrows so the modal can name the
   * refusal (an in-stock card, a guest asking for ongoing updates).
   */
  const subscribeProductUpdates = useCallback(async (cardId: string, input: ProductUpdateSubscribeInput): Promise<unknown> => {
    return await handleRequest(() => cardsGateway.subscribeProductUpdates(cardId, input), { rethrow: true });
  }, [cardsGateway, handleRequest]);

  /**
   * Stops one of the signed-in customer's subscriptions (the modal's Stop).
   * The manage block and the bell's hook call the gateway directly.
   */
  const stopAccountUpdate = useCallback(async (productId: string, sku?: string): Promise<ProductUpdateSubscriptionView[] | null> => {
    return await handleRequest(() => cardsGateway.stopAccountUpdate(productId, sku), { rethrow: true });
  }, [cardsGateway, handleRequest]);

  return {
    cards,
    card,
    isLoading,
    error,
    getCards,
    getCardByBrand,
    getCategories,
    getCountries,
    searchCards,
    getCardsWithSameBrand,
    subscribeProductUpdates,
    stopAccountUpdate,
  };
};
